import React, { useEffect, useState } from "react";
import PrivateChatPanel from "./private_chat_panel";
import { Participant } from "./helpers";
import { useChat } from "./use_chat";
import useChannel from "./use_channel";
import { useOpenChatFromQueryParams } from "./use_open_chat_from_query_params";
import { InlineButton } from "./inline_button";
import { ActionButton } from "./action_button";
import { useChatContext } from "./chat_client_context";
import { Channel } from "stream-chat";
import { ChatType } from "types/app";

export enum ButtonType {
  Inline = "inline",
  Action = "action",
}

type OpenPrivateChatButtonProps = {
  type: ChatType;
  testName: string;
  testId: string;
  testCode: string;
  participant: Participant;
  variant: ButtonType;
};

function OpenPrivateChatButton({
  type,
  testName,
  testId,
  testCode,
  participant,
  variant = ButtonType.Inline,
}: OpenPrivateChatButtonProps) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { isLoading: isClientLoading } = useChat();
  const { participantsWithPrivateChannel } = useChatContext();
  const [privateChannel, setPrivateChannel] = useState<Channel>();

  useEffect(() => {
    const participantWithChannel = participantsWithPrivateChannel.get(participant.id);

    if (participantWithChannel) {
      setPrivateChannel(participantsWithPrivateChannel.get(participant.id));
      setShouldInitializeChat(true);
    }
  }, [participantsWithPrivateChannel]);

  // We want to be careful with initializing private chat, because it can cause a lot of requests
  // to the server. We want to lazy initialize chat only when the user opens the chat panel or
  // the private channel already exists for a given participant.
  const [shouldInitializeChat, setShouldInitializeChat] = useState(false);

  const {
    unreadMessagesCount,
    channel,
    isLoading: isPrivateChatLoading,
  } = useChannel({
    targetId: testId,
    chatType: type,
    testerId: participant.testerId,
    providedChannel: privateChannel,
    shouldInitialize: shouldInitializeChat,
  });
  const { shouldOpenChannel, messageToJumpIntoExternalId } = useOpenChatFromQueryParams(channel?.id);

  useEffect(() => {
    if (shouldOpenChannel) {
      setIsChatOpen(true);
    }
  }, [shouldOpenChannel]);

  const onChatButtonClick = () => {
    setIsChatOpen(!isChatOpen);
    setShouldInitializeChat(true);
  };

  return (
    <>
      {variant === ButtonType.Inline && (
        <InlineButton onChatButtonClick={onChatButtonClick} unreadMessagesCount={unreadMessagesCount} />
      )}

      {variant === ButtonType.Action && (
        <ActionButton onChatButtonClick={onChatButtonClick} unreadMessagesCount={unreadMessagesCount} />
      )}

      {isChatOpen && (
        <PrivateChatPanel
          isChatOpen={isChatOpen}
          chatTitle={testName}
          chatSubtitle={testCode}
          userFullName={participant.fullName}
          onChatClose={() => setIsChatOpen(false)}
          channel={channel}
          isLoading={isClientLoading || isPrivateChatLoading}
          messageToJumpIntoExternalId={messageToJumpIntoExternalId}
        />
      )}
    </>
  );
}

export default OpenPrivateChatButton;
